import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Link } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import portfolioData from './data/portfolio-data.json';

// Import orphan prevention utility
import { preventOrphans, parseMarkdown } from './utils/textUtils';

// Import components
import ArtworkPage from './components/ArtworkPage';
import TemplateFrame from './components/TemplateFrame';
import AboutPage from './components/AboutPage';
import NewsPage from './components/NewsPage';
import AdminPage from './components/AdminPage';
import Navigation from './components/Navigation';
import GalleryCarouselPage from './components/GalleryCarouselPage';
import Amplify3Page from './components/Amplify3Page';
import Amplify3DownloadPage from './components/Amplify3DownloadPage';

// Google Sheets CSV URLs - Carousel, Assets, Galleries, News
const CAROUSEL_SHEET_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQZTLeP4sJrYbvA7H3xxARywowmdJKhw82XxogolYsoarxkfOeQfyJCag_xkHMtHR2wCOIalvtKXg1m/pub?gid=2067140409&single=true&output=csv';
const ASSETS_SHEET_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQZTLeP4sJrYbvA7H3xxARywowmdJKhw82XxogolYsoarxkfOeQfyJCag_xkHMtHR2wCOIalvtKXg1m/pub?gid=1280232217&single=true&output=csv';
const GALLERIES_SHEET_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQZTLeP4sJrYbvA7H3xxARywowmdJKhw82XxogolYsoarxkfOeQfyJCag_xkHMtHR2wCOIalvtKXg1m/pub?gid=1082273483&single=true&output=csv';
const NEWS_SHEET_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQZTLeP4sJrYbvA7H3xxARywowmdJKhw82XxogolYsoarxkfOeQfyJCag_xkHMtHR2wCOIalvtKXg1m/pub?gid=1580558307&single=true&output=csv';

// Assets base URL
const ASSETS_BASE_URL = 'https://dyararts.com';


// Helper function to build asset URL
const buildAssetUrl = (filename, mobileFilename = '') => {
  if (!filename) return '';
  
  const isMobile = window.innerWidth <= 768;
  if (isMobile && mobileFilename) {
    return `${ASSETS_BASE_URL}${mobileFilename}`;
  }
  
  return `${ASSETS_BASE_URL}${filename}`;
};

// Helper function to determine asset type from filename
const getAssetType = (filename, mobileFilename = '') => {
  const isMobile = window.innerWidth <= 768;
  const fileToCheck = (isMobile && mobileFilename) ? mobileFilename : filename;
  
  if (!fileToCheck) return 'image';
  const extension = fileToCheck.toLowerCase().split('.').pop();
  const videoExtensions = ['mp4', 'webm', 'ogg', 'avi', 'mov'];
  return videoExtensions.includes(extension) ? 'video' : 'image';
};

// Helper function to convert asset to slide object
const assetToSlide = (asset, project, index) => ({
  id: `${project['project-id']}-${index + 1}`,
  type: asset.type || getAssetType(asset.filename, ''),
  src: buildAssetUrl(asset.filename, ''),
  alt: asset['alt-text'] || asset.title || `${project.name} - ${asset.filename}`,
  title: asset.title || `Slide ${index + 1}`,
  captionTitle: asset.title || `Slide ${index + 1}`,
  captionDescription: asset.description || '',
  subHeadline: asset['sub-headline'] || '',
  mediaId: asset.mediaId || `media${index + 1}`,
  backgroundSize: asset['background-size'] || '',
  size: asset.size || '110%',
  fit: asset.fit || 'contain',
  orientation: asset.orientation || 'vertical',
  'hero-assets': asset['hero-assets'] || 'none',
  'hero-background': asset['hero-background'] || '',
  'hero-background-tiny': asset['hero-background-tiny'] || '',
  'artwork-id': asset['artwork-id'] || asset.filename.split('_')[0],
  slug: asset.slug || ''
});

// Helper function to build DESKTOP slides (individual slides only)
const buildDesktopSlides = (project, assetsData) => {
  const projectAssets = assetsData.filter(asset =>
    asset && asset['project-id'] === project['project-id']
  );

  if (projectAssets.length === 0) return [];

  return projectAssets.map((asset, index) => assetToSlide(asset, project, index));
};

// Helper function to build MOBILE slides (all individual)
const buildMobileSlides = (project, assetsData) => {
  const projectAssets = assetsData.filter(asset => 
    asset && asset['project-id'] === project['project-id']
  );
  
  if (projectAssets.length === 0) return [];
  
  return projectAssets.map((asset, index) => assetToSlide(asset, project, index));
};

// Generic function to build carousel slides based on filter-method
const buildCarouselSlides = (project, assetsData, heroIds = []) => {
  if (!project) return [];
  
  const filterMethod = project['filter-method'];
  let filteredAssets = [];
  
  if (filterMethod === 'hero-order') {
    // Special case: use hand-picked hero-order from Assets sheet
    if (!heroIds || heroIds.length === 0) return [];
    
    // Build slides in the exact order specified by heroIds array
    heroIds.forEach((heroId, index) => {
      const asset = assetsData.find(a => {
        const filename = a.filename || '';
        const filenameOnly = filename.split('/').pop() || filename;
        return filenameOnly.startsWith(heroId) || filename === heroId || filenameOnly === heroId;
      });
      
      if (asset) {
        filteredAssets.push(asset);
      } else {
        console.warn(`Asset not found for hero-order: ${heroId}`);
      }
    });
  } else if (filterMethod) {
    // Filter by prefix (e.g., 'ink', 'art')
    filteredAssets = assetsData.filter(asset => {
      const artworkId = asset['artwork-id'] || asset.filename.split('_')[0];
      return artworkId.startsWith(filterMethod);
    });
    
    // Sort by artwork-id to ensure correct order
    filteredAssets.sort((a, b) => {
      const idA = a['artwork-id'] || '';
      const idB = b['artwork-id'] || '';
      return idA.localeCompare(idB);
    });
  }
  
  // Convert assets to slides
  return filteredAssets.map((asset, index) => assetToSlide(asset, project, index));
};

// Masonry Item Component - Individual gallery item with image loading
function MasonryItem({ asset, baseUrl }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState('');

  // Helper function to generate tiny image URL
  const getTinyImageUrl = useCallback((filename) => {
    const ext = filename.substring(filename.lastIndexOf('.'));
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
    return `${baseUrl}${nameWithoutExt}_tiny${ext}`;
  }, [baseUrl]);

  // Image loading effect: tiny -> full
  useEffect(() => {
    if (!asset) return;

    const fullImageUrl = `${baseUrl}${asset.filename}`;
    const tinyImageUrl = getTinyImageUrl(asset.filename);

    // Reset loading state
    setImageLoaded(false);
    setCurrentImageUrl(tinyImageUrl);

    // Preload the full image
    const img = new Image();
    img.src = fullImageUrl;
    img.onload = () => {
      setCurrentImageUrl(fullImageUrl);
      setImageLoaded(true);
    };
  }, [asset, baseUrl, getTinyImageUrl]);

  const imageUrl = `${baseUrl}${asset.filename}`;
  const isHorizontal = asset.orientation === 'horizontal';

  // Extract art ID from artwork-id or filename
  const artId = asset['artwork-id'] || asset.filename.split('_')[0];

  // Use slug for "art" prefixed artworks, otherwise use hash
  let linkUrl = `#${artId}`;
  if (artId.startsWith('art')) {
    if (asset.slug) {
      // Normalize slug: convert underscores to hyphens and lowercase
      const normalizedSlug = asset.slug.replace(/_/g, '-').toLowerCase();
      linkUrl = `/art/${normalizedSlug}`;
    } else {
      // Fallback: use artwork ID directly if no slug
      linkUrl = `/art/${artId}`;
    }
  }

  // Helper function to get font size class based on title length
  const getTitleSizeClass = (title) => {
    if (!title) return '';
    const words = title.split(' ');
    const longestWord = Math.max(...words.map(word => word.length));

    // Only check longest individual word, not total length
    if (longestWord > 12) return 'masonry-title-small';
    if (longestWord > 8) return 'masonry-title-medium';
    return '';
  };

  return (
    <a
      href={linkUrl}
      className={`masonry-item ${isHorizontal ? 'masonry-item-horizontal' : ''}`}
    >
      <div
        className={`masonry-item-background ${!imageLoaded ? 'loading-tiny' : ''}`}
        style={{ backgroundImage: `url(${currentImageUrl})` }}
      >
        {/* Structural img provides sizing, background-image provides visual display at 106% */}
        <img
          src={imageUrl}
          alt={asset['alt-text'] || asset.title || 'Artwork'}
          className="masonry-structural-image"
        />
      </div>
      <div className="masonry-item-overlay">
        <h3 className={getTitleSizeClass(asset.title)}>{asset.title}</h3>
        {asset['sub-headline'] && (
          <p className="masonry-sub-headline">{preventOrphans(asset['sub-headline'], 6)}</p>
        )}
      </div>
    </a>
  );
}

// Masonry Gallery Component
function MasonryGallery({ assets, filterPrefix, sortOrder = 'latest' }) {
  // Filter assets by prefix if provided
  const filteredAssets = filterPrefix
    ? assets.filter(asset => {
        const artId = asset['artwork-id'] || asset.filename.split('_')[0];
        return artId.startsWith(filterPrefix);
      })
    : assets;

  // Sort filtered assets by artwork-id only
  const sortedAssets = [...filteredAssets].sort((a, b) => {
    const idA = a['artwork-id'] || '';
    const idB = b['artwork-id'] || '';

    if (sortOrder === 'latest') {
      // Latest first: reverse order (descending by ID)
      return idB.localeCompare(idA);
    } else {
      // Earliest first: normal order (ascending by ID)
      return idA.localeCompare(idB);
    }
  });

  if (!sortedAssets || sortedAssets.length === 0) {
    return <div className="text-center py-8 text-gray-500">No artwork available</div>;
  }

  return (
    <div className="masonry-gallery">
      {sortedAssets.map((asset, index) => (
        <MasonryItem
          key={asset.filename || index}
          asset={asset}
          baseUrl={ASSETS_BASE_URL}
        />
      ))}
    </div>
  );
}

// Helper function to build slides from project data and assets
const buildProjectSlides = (project, assetsData) => {
  try {
    // Safety check for required data
    if (!project || !project['project-id']) {
      console.error('Invalid project data:', project);
      return [{
        id: 'error-slide',
        type: 'image',
        src: 'https://via.placeholder.com/800x600/ff0000/ffffff?text=Error',
        alt: 'Error loading project',
        title: 'Error',
        description: 'Invalid project data'
      }];
    }

    // Safety check for assets data
    if (!assetsData || !Array.isArray(assetsData)) {
      return [{
        id: `${project['project-id']}-placeholder`,
        type: 'image',
        src: 'https://via.placeholder.com/800x600/cccccc/666666?text=Loading+Assets...',
        alt: `${project.name} - Loading Assets`,
        title: 'Loading assets...',
        description: 'Assets are being loaded from the spreadsheet'
      }];
    }

    // Filter assets for this specific project
    const projectAssets = assetsData.filter(asset => 
      asset && asset['project-id'] === project['project-id']
    );
    
    
    // If no assets found, return placeholder
    if (projectAssets.length === 0) {
      return [{
        id: `${project['project-id']}-placeholder`,
        type: 'image',
        src: 'https://via.placeholder.com/800x600/cccccc/666666?text=No+Assets+Found',
        alt: `${project.name} - No Assets`,
        title: 'No assets found',
        description: 'Assets will be loaded when available'
      }];
    }
    
    // Build slides from assets data, maintaining spreadsheet order
    return projectAssets.map((asset, index) => ({
      id: `${project['project-id']}-${index + 1}`,
      type: asset.type || getAssetType(asset.filename, ''),
      src: buildAssetUrl(asset.filename, ''),
      alt: asset.alt || `${project.name} - ${asset.filename}`,
      title: asset.title || `Slide ${index + 1}`,
      captionTitle: asset.title || `Slide ${index + 1}`,
      captionDescription: asset.description || '',
      mediaId: asset.mediaId || `media${index + 1}`,
      backgroundSize: asset['background-size'] || '',
      size: asset.size || '110%',
      fit: asset.fit || 'contain',
      orientation: asset.orientation || 'vertical',
      'artwork-id': asset['artwork-id'] || asset.filename.split('_')[0],
      slug: asset.slug || ''
    }));
  } catch (error) {
    console.error('Error building project slides:', error);
    return [{
      id: 'error-slide',
      type: 'image',
      src: 'https://via.placeholder.com/800x600/ff0000/ffffff?text=Error',
      alt: 'Error loading slides',
      title: 'Error',
      description: 'Failed to load slides'
    }];
  }
};

// CSV Parser - Handles quoted fields with commas AND newlines properly
const parseCSV = (csvText) => {
  const rows = [];
  let currentRow = [];
  let currentField = '';
  let inQuotes = false;

  // Parse character by character to handle multi-line quoted fields
  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote ("") - add one quote and skip next
        currentField += '"';
        i++;
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      currentRow.push(currentField.trim());
      currentField = '';
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      // End of row (handle both \n and \r\n)
      if (char === '\r' && nextChar === '\n') {
        i++; // Skip the \n in \r\n
      }
      // Only add row if it has content
      if (currentField || currentRow.length > 0) {
        currentRow.push(currentField.trim());
        if (currentRow.some(field => field !== '')) {
          rows.push(currentRow);
        }
        currentRow = [];
        currentField = '';
      }
    } else {
      // Regular character (including newlines inside quotes)
      currentField += char;
    }
  }

  // Handle last field and row
  if (currentField || currentRow.length > 0) {
    currentRow.push(currentField.trim());
    if (currentRow.some(field => field !== '')) {
      rows.push(currentRow);
    }
  }

  if (rows.length === 0) return [];

  // First row is headers
  const headers = rows[0];
  const data = [];

  // Convert remaining rows to objects
  for (let i = 1; i < rows.length; i++) {
    const row = {};
    headers.forEach((header, index) => {
      row[header] = rows[i][index] || '';
    });
    data.push(row);
  }

  return data;
};

// Fetch and parse Carousel data from Google Sheets (renamed from Projects)
const fetchCarouselData = async () => {
  try {
    const response = await fetch(CAROUSEL_SHEET_URL);
    
    if (!response.ok) {
      console.error('HTTP Error:', response.status, response.statusText);
      console.error('Response URL:', response.url);
      return [];
    }
    
    const csvText = await response.text();
    
    if (csvText.includes('<html>') || csvText.includes('<!DOCTYPE')) {
      console.error('Got HTML response instead of CSV - spreadsheet may not be published');
      return [];
    }
    
    const parsed = parseCSV(csvText);
    return parsed;
  } catch (error) {
    console.error('Error fetching carousel data:', error);
    return [];
  }
};

// Fetch and parse Assets data from Google Sheets
const fetchAssetsData = async () => {
  try {
    const response = await fetch(ASSETS_SHEET_URL);
    
    if (!response.ok) {
      console.error('HTTP Error:', response.status, response.statusText);
      console.error('Response URL:', response.url);
      return [];
    }
    
    const csvText = await response.text();
    
    if (csvText.includes('<html>') || csvText.includes('<!DOCTYPE')) {
      console.error('Got HTML response instead of CSV - spreadsheet may not be published');
      return [];
    }
    
    const parsed = parseCSV(csvText);
    return parsed;
  } catch (error) {
    console.error('Error fetching assets data:', error);
    return [];
  }
};

// Fetch and parse Galleries data from Google Sheets
const fetchGalleriesData = async () => {
  try {
    const response = await fetch(GALLERIES_SHEET_URL);
    
    if (!response.ok) {
      console.error('HTTP Error:', response.status, response.statusText);
      console.error('Response URL:', response.url);
      return [];
    }
    
    const csvText = await response.text();
    
    if (csvText.includes('<html>') || csvText.includes('<!DOCTYPE')) {
      console.error('Got HTML response instead of CSV - spreadsheet may not be published');
      return [];
    }
    
    const parsed = parseCSV(csvText);
    return parsed;
  } catch (error) {
    console.error('Error fetching galleries data:', error);
    return [];
  }
};

// Fetch and parse News data from Google Sheets
const fetchNewsData = async () => {
  try {
    const response = await fetch(NEWS_SHEET_URL);

    if (!response.ok) {
      console.error('HTTP Error:', response.status, response.statusText);
      console.error('Response URL:', response.url);
      return [];
    }

    const csvText = await response.text();

    if (csvText.includes('<html>') || csvText.includes('<!DOCTYPE')) {
      console.error('Got HTML response instead of CSV - spreadsheet may not be published');
      return [];
    }

    const parsed = parseCSV(csvText);

    // Filter only live news items and map fields (natural row order)
    const liveNews = parsed
      .filter(item => item['Live?'] === 'TRUE' || item['live?'] === 'TRUE' || item['Live'] === 'TRUE')
      .map(item => ({
        'Date': item['Date'] || item['date'] || '',
        'Headline': item['Headline'] || item['headline'] || '',
        'Content': item['Content'] || item['content'] || '',
        'Link': item['Link'] || item['link'] || ''
      }));

    return liveNews;
  } catch (error) {
    console.error('Error fetching news data:', error);
    return [];
  }
};

// Helper function to generate artwork link URL from slide
const getSlideLink = (slide) => {
  const artId = slide['artwork-id'];

  // Only create links for "art" prefixed artworks
  if (artId && artId.startsWith('art')) {
    let path;
    if (slide.slug) {
      // Normalize slug: convert underscores to hyphens and lowercase
      const normalizedSlug = slide.slug.replace(/_/g, '-').toLowerCase();
      path = `/art/${normalizedSlug}`;
    } else {
      // Fallback: generate slug from artwork ID
      path = `/art/${artId}`;
    }
    // Add query parameter to indicate user came from carousel
    return `${path}?from=carousel`;
  }

  return null;
};

// Carousel Component
function Carousel({ slides, title, projectId, project, autoplayDuration = 5000, isGlobalMuted, toggleMenu, isGlobalAutoplayActive, toggleGlobalAutoplay, activeCarouselId, carouselPositions, updateCarouselPosition, getCurrentSlide, isPaginationMinimal, newsData, assetsData }) {
  // Helper function to get thumbnail URL from artwork ID
  const getThumbnailUrl = (artworkId) => {
    if (!artworkId || !assetsData) return null;
    const artwork = assetsData.find(asset => asset['artwork-id'] === artworkId);
    return artwork?.thumbnail ? `${ASSETS_BASE_URL}${artwork.thumbnail}` : null;
  };
  // Use global position tracking instead of local state
  const currentSlide = getCurrentSlide(projectId);
  const [autoplayProgress, setAutoplayProgress] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [videoAspectRatio, setVideoAspectRatio] = useState(null);
  const [carouselHeightClass, setCarouselHeightClass] = useState('carousel-height-image');

  // Image loading state for carousel assets
  const [assetImageLoaded, setAssetImageLoaded] = useState(false);
  const [assetCurrentImageUrl, setAssetCurrentImageUrl] = useState('');

  // Pagination state
  const [paginationScrollLeft, setPaginationScrollLeft] = useState(0);

  // Video controls visibility state - container always present, content conditional
  const [showVideoControls, setShowVideoControls] = useState(false);

  // Audio detection state
  const [hasAudioTrack, setHasAudioTrack] = useState(true);
  
  // Local mute state - starts with global state but can be toggled independently
  const [isLocalMuted, setIsLocalMuted] = useState(isGlobalMuted);

  const videoRef = useRef(null);
  
  const carouselRef = useRef(null);
  const paginationContainerRef = useRef(null);

  // Constants
  const PROGRESS_INCREMENT = 1; // 1% every 50ms
  const PROGRESS_INTERVAL = 50; // 50ms
  const VISIBLE_BUTTONS = 5; // Number of buttons visible at once
  const BUTTON_WIDTH = 64; // Width of each button in pixels (w-16 = 64px)

  // Sync local mute state with global mute state
  useEffect(() => {
    setIsLocalMuted(isGlobalMuted);
  }, [isGlobalMuted]);

  // Local mute toggle function
  const toggleLocalMute = () => {
    setIsLocalMuted(prev => {
      const next = !prev;
      if (videoRef.current) videoRef.current.muted = next;
      return next;
    });
  };

  // Helper function to generate tiny image URL
  const getTinyImageUrl = useCallback((filename) => {
    if (!filename) return '';
    const ext = filename.substring(filename.lastIndexOf('.'));
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
    return `${ASSETS_BASE_URL}${nameWithoutExt}_tiny${ext}`;
  }, []);

  // Image loading effect for carousel assets: tiny -> full
  useEffect(() => {
    console.log('🔍 Carousel useEffect triggered', { currentSlide, slidesLength: slides.length });

    if (currentSlide >= slides.length) {
      console.log('⚠️ currentSlide >= slides.length, returning');
      return;
    }

    const slide = slides[currentSlide];
    console.log('🔍 Current slide:', {
      type: slide.type,
      heroAssets: slide['hero-assets'],
      src: slide.src,
      title: slide.title
    });

    // Skip video slides (process all non-video slides: Drawing, Painting, etc.)
    if (slide.type === 'video') {
      console.log('⚠️ Video slide, skipping image loading');
      return;
    }

    const showAsset = slide['hero-assets'] === 'none' || slide['hero-assets'] === 'both';
    console.log('🔍 showAsset check:', { showAsset, heroAssets: slide['hero-assets'], hasSrc: !!slide.src });

    if (!showAsset || !slide.src) {
      console.log('⚠️ showAsset is false or no src, returning');
      return;
    }

    const fullImageUrl = slide.src;
    const filename = slide.src.replace(ASSETS_BASE_URL, '');
    const tinyImageUrl = getTinyImageUrl(filename);

    console.log('🎨 Carousel image loading:', {
      fullImageUrl,
      tinyImageUrl,
      filename,
      slideTitle: slide.title
    });

    // Reset loading state
    setAssetImageLoaded(false);
    setAssetCurrentImageUrl(tinyImageUrl);

    // Preload the full image
    const img = new Image();
    img.src = fullImageUrl;
    img.onload = () => {
      console.log('✅ Full carousel image loaded:', fullImageUrl);
      setAssetCurrentImageUrl(fullImageUrl);
      setAssetImageLoaded(true);
    };
    img.onerror = () => {
      console.error('❌ Failed to load full carousel image:', fullImageUrl);
      // Keep showing tiny image if full image fails
    };
  }, [currentSlide, slides, getTinyImageUrl]);

  // Autoplay timing - completely separate from video controls
  const SLIDES_AUTOPLAY_INTERVAL = 3000; // 5 seconds, easily changeable

  // Navigation functions
  const nextSlide = useCallback(() => {
    const newSlide = currentSlide < slides.length - 1 
      ? currentSlide + 1
      : 0; // Loop back to first slide
    

    
    // Defer state update to avoid React warning about updating during render
    setTimeout(() => {
      updateCarouselPosition(projectId, newSlide);
    }, 0);
    resetAutoplay();
    resetVideo();
  }, [currentSlide, slides.length, projectId, updateCarouselPosition, slides]);

  // Autoplay functionality - completely separate from video controls
  // Now works with global autoplay state AND only for visible carousel
  // Video-aware: only advances on image slides, skips video slides
  useEffect(() => {
    let interval;
    if (false) console.log(`DEBUG: Carousel ${projectId} - isGlobalAutoplayActive: ${isGlobalAutoplayActive}, activeCarouselId: ${activeCarouselId}, projectId: ${projectId}`);
    
    if (isGlobalAutoplayActive && activeCarouselId === projectId) {
      if (false) console.log(`DEBUG: Starting autoplay for carousel ${projectId}`);
      interval = setInterval(() => {
        setAutoplayProgress(prev => {
          if (prev >= 100) {
            // Check if current slide is a video before advancing
            const currentSlideIndex = getCurrentSlide(projectId);
            const isCurrentSlideVideo = currentSlideIndex < slides.length && slides[currentSlideIndex].type === 'video';
            
            if (isCurrentSlideVideo) {
              return 0; // Reset progress and wait
            } else {
              nextSlide();
              return 0;
            }
          }
          return prev + PROGRESS_INCREMENT;
        });
      }, SLIDES_AUTOPLAY_INTERVAL / 100); // Divide by 100 to get progress update interval
    } else {
      if (false) console.log(`DEBUG: Not starting autoplay for carousel ${projectId} - conditions not met`);
    }
    return () => clearInterval(interval);
  }, [isGlobalAutoplayActive, activeCarouselId, projectId, PROGRESS_INCREMENT, SLIDES_AUTOPLAY_INTERVAL, nextSlide, getCurrentSlide, slides]);

  // Automatically stop autoplay when arriving at video slides
  useEffect(() => {
    if (currentSlide < slides.length && slides[currentSlide].type === 'video') {
      setAutoplayProgress(0);
    }
  }, [currentSlide, slides]);

  // Stop autoplay when global autoplay is turned off
  useEffect(() => {
    if (!isGlobalAutoplayActive) {
      setAutoplayProgress(0);
    }
  }, [isGlobalAutoplayActive]);

  // Video progress tracking
  useEffect(() => {
    if (currentSlide < slides.length && slides[currentSlide].type === 'video' && videoRef.current) {
      const video = videoRef.current;
      const updateProgress = () => {
        if (video.duration) {
          setVideoProgress((video.currentTime / video.duration) * 100);
        }
      };
      
      video.addEventListener('timeupdate', updateProgress);
      return () => video.removeEventListener('timeupdate', updateProgress);
    }
  }, [currentSlide, slides]);

  // Detect video aspect ratio when video loads
  useEffect(() => {
    if (currentSlide < slides.length && slides[currentSlide].type === 'video' && videoRef.current) {
      const video = videoRef.current;
      
      const handleLoadedMetadata = () => {
        const aspectRatio = detectVideoAspectRatio(video);
        setVideoAspectRatio(aspectRatio);
      };
      
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      
      // Check if metadata is already loaded
      if (video.videoWidth > 0) {
        handleLoadedMetadata();
      }
      
      return () => video.removeEventListener('loadedmetadata', handleLoadedMetadata);
    } else {
      setVideoAspectRatio(null);
    }
  }, [currentSlide, slides]);

  // Handle video controls visibility - simple check on every slide change
  useEffect(() => {
    const isVideoSlide = currentSlide < slides.length && slides[currentSlide].type === 'video';
    setShowVideoControls(isVideoSlide);
  }, [currentSlide, slides]);


  // Audio track detection from spreadsheet data
  const handleVideoLoadedMetadata = useCallback(() => {
    if (currentSlide < slides.length) {
      const currentSlideData = slides[currentSlide];
      
      // Assume all videos may have audio
      const hasAudio = currentSlideData.type === 'video';
      setHasAudioTrack(hasAudio);
    }
  }, [currentSlide, slides]);

  // Pause all videos when leaving this carousel
  const pauseAllVideos = useCallback(() => {
    // Pause the current carousel's video
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
      setIsVideoPlaying(false);
    }
    
    // Pause all other videos in the document as a safety measure
    const allVideos = document.querySelectorAll('video');
    allVideos.forEach(video => {
      if (!video.paused) {
        video.pause();
      }
    });
  }, []);


  // Global mouse events for smooth video scrubbing
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e) => {
        const scrubberContainer = document.querySelector(`.section-container[data-project="${projectId}"] .video-scrubber-container`);
        if (scrubberContainer) {
          const rect = scrubberContainer.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          const percentage = Math.max(0, Math.min(100, (clickX / rect.width) * 100));
          const newTime = (percentage / 100) * videoRef.current.duration;
          
          if (videoRef.current && videoRef.current.duration) {
            videoRef.current.currentTime = newTime;
            setVideoProgress(percentage);
          }
        }
      };

      const handleGlobalMouseUp = () => {
        setIsDragging(false);
      };

      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, projectId]);

  // Update video class when screen size changes
  const videoResizeTimeoutRef = useRef(null);
  
  useEffect(() => {
    const handleResize = () => {
      // Clear any pending timeout
      if (videoResizeTimeoutRef.current) {
        clearTimeout(videoResizeTimeoutRef.current);
      }
      
      // Debounce: wait 150ms after resize stops before processing
      videoResizeTimeoutRef.current = setTimeout(() => {
        // Force re-render to update video class
        if (videoRef.current && currentSlide < slides.length && slides[currentSlide].type === 'video') {
          // Trigger a re-render by updating a dummy state or forcing class update
          const video = videoRef.current;
          const newClass = getVideoResponsiveClass();
          video.className = `carousel-video ${newClass}`;
        }
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (videoResizeTimeoutRef.current) {
        clearTimeout(videoResizeTimeoutRef.current);
      }
    };
  }, [videoAspectRatio, currentSlide, slides]);

  // Pagination scroll logic
  const updatePaginationScroll = useCallback((activeIndex) => {
    // Total buttons = slides only
    const totalButtons = slides.length;
    
    if (totalButtons <= VISIBLE_BUTTONS) {
      setPaginationScrollLeft(0);
      return;
    }
    
    // Calculate which "page" of buttons we should show
    const pageIndex = Math.floor(activeIndex / VISIBLE_BUTTONS);
    const scrollLeft = pageIndex * VISIBLE_BUTTONS * BUTTON_WIDTH;
    setPaginationScrollLeft(scrollLeft);
  }, [slides.length]);

  // Initialize pagination scroll position
  useEffect(() => {
    updatePaginationScroll(currentSlide);
  }, [currentSlide, updatePaginationScroll]);

  // Scroll the pagination container when paginationScrollLeft changes
  useEffect(() => {
    if (paginationContainerRef.current) {
      paginationContainerRef.current.scrollLeft = paginationScrollLeft;
    }
  }, [paginationScrollLeft]);



  const goToSlide = (index) => {
    // Ensure index is within valid range (0 to slides.length)
    const validIndex = Math.max(0, Math.min(index, slides.length));
    updateCarouselPosition(projectId, validIndex);
    resetAutoplay();
    resetVideo();
    updatePaginationScroll(validIndex);
    
    // Stop autoplay if arriving at a video slide
    if (validIndex < slides.length && slides[validIndex].type === 'video') {
      setAutoplayProgress(0); // Reset progress to 0
    }
  };



  const toggleAutoplay = () => {
    // Control global autoplay state instead of local state
    toggleGlobalAutoplay();
  };

  const resetAutoplay = () => {
    setAutoplayProgress(0);
  };

  const resetVideo = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      setVideoProgress(0);
      setIsVideoPlaying(false);
    }
  };

  const toggleVideoPlayback = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };


  // Video scrubbing with smooth drag functionality
  const handleVideoSeek = (e) => {
    if (videoRef.current && videoRef.current.duration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (clickX / rect.width) * 100));
      const newTime = (percentage / 100) * videoRef.current.duration;
      
      videoRef.current.currentTime = newTime;
      setVideoProgress(percentage);
    }
  };

  const handleMouseDown = (e) => {
    if (videoRef.current && videoRef.current.duration) {
      setIsDragging(true);
      handleVideoSeek(e);
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && videoRef.current && videoRef.current.duration) {
      handleVideoSeek(e);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Detect video aspect ratio and apply responsive class
  const detectVideoAspectRatio = (video) => {
    if (!video) return null;
    
    const aspectRatio = video.videoWidth / video.videoHeight;
    
    // Determine if it's 16:9 or 1:1 (with some tolerance)
    if (Math.abs(aspectRatio - 16/9) < 0.1) {
      return '16-9';
    } else if (Math.abs(aspectRatio - 1) < 0.1) {
      return '1-1';
    }
    
    return '16-9'; // Default fallback
  };

  // Get responsive video class based on aspect ratio and screen size
  const getVideoResponsiveClass = () => {
    if (!videoAspectRatio) return 'video-responsive';
    
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      return videoAspectRatio === '1-1' ? 'video-1-1-mobile' : 'video-16-9-mobile';
    } else {
      return videoAspectRatio === '1-1' ? 'video-1-1-desktop' : 'video-16-9-desktop';
    }
  };


  const handleVideoEnded = () => {
    setIsVideoPlaying(false);
    setVideoProgress(0);
    // Always go to next slide when video ends, regardless of autoplay state
    const nextSlideIndex = currentSlide < slides.length - 1 
      ? currentSlide + 1
      : 0; // Loop back to first slide
    
    goToSlide(nextSlideIndex);
  };

  // Render slide content
  const renderSlideContent = (slide) => {
    if (slide.type === 'video') {
      return (
        <div className="relative w-full h-full">
            <video
              ref={videoRef}
              src={slide.src}
              className="carousel-video"
              muted={isLocalMuted}
            autoPlay
            onEnded={handleVideoEnded}
            onPlay={() => setIsVideoPlaying(true)}
            onPause={() => setIsVideoPlaying(false)}
            onClick={toggleVideoPlayback}
            onLoadStart={() => console.log('Loading video:', slide.src)}
            onCanPlay={() => console.log('Video loaded:', slide.src)}
            onLoadedMetadata={handleVideoLoadedMetadata}
            onError={() => console.log('Video failed to load:', slide.src)}
            data-project={projectId}
            data-media={slide.mediaId}
            data-media-type="video"
            data-media-title={slide.title}
            style={{ cursor: 'pointer' }}
          />
          <div className="loading-media" style={{ display: 'none' }}>
            Loading media...
          </div>
        </div>
      );
    }
    
    // Single image slide with optional background
    // Default "none" or empty = show asset only (regular behavior)
    // Valid values: "none" (asset only), "background" (bg only), "both" (asset + bg)
    const heroAssets = slide['hero-assets'] || 'none';
    const heroBackground = slide['hero-background'] || '';

    const showAsset = heroAssets === 'none' || heroAssets === 'both';
    const showBackground = heroAssets === 'background' || heroAssets === 'both';
    const hasBackgroundError = showBackground && !heroBackground;

    return (
      <div className="relative w-full h-full">
        {/* Background layer (if needed) with blur-up effect */}
        {showBackground && (
          <div className={`carousel-background ${hasBackgroundError ? 'background-error' : ''}`}>
            {!hasBackgroundError && (
              <>
                {/* Tiny blurred background - shows immediately (but only if full image is not cached) */}
                {slide['hero-background-tiny'] && (
                  <img
                    key={`tiny-${slide.id}`}
                    src={`${ASSETS_BASE_URL}${slide['hero-background-tiny']}`}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      filter: 'blur(20px)',
                      transform: 'scale(1.1)',
                      transition: 'opacity 0.4s ease-in-out, filter 0.4s ease-in-out, transform 0.4s ease-in-out'
                    }}
                  />
                )}
                {/* Full resolution background - fades in when loaded (or shows immediately if cached) */}
                <img
                  key={`full-${slide.id}`}
                  src={`${ASSETS_BASE_URL}${heroBackground}`}
                  onLoad={(e) => {
                    // Check if image was already cached (loaded instantly)
                    const wasCached = e.target.complete && e.target.naturalWidth > 0;

                    // Fade in full image
                    e.target.style.opacity = '1';

                    // Only fade out tiny image if this wasn't from cache
                    // (if cached, tiny image should already be hidden)
                    const tinyImg = e.target.previousSibling;
                    if (tinyImg && !wasCached) {
                      tinyImg.style.opacity = '0';
                    }
                  }}
                  ref={(img) => {
                    if (img) {
                      // Check if image is already cached when element is mounted
                      const isCached = img.complete && img.naturalWidth > 0;
                      if (isCached) {
                        // Image is cached, show it immediately and hide tiny image
                        img.style.opacity = '1';
                        img.style.transition = 'none'; // No transition for cached images
                        const tinyImg = img.previousSibling;
                        if (tinyImg) {
                          tinyImg.style.opacity = '0';
                          tinyImg.style.display = 'none'; // Don't even show it
                        }
                      }
                    }
                  }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    opacity: slide['hero-background-tiny'] ? 0 : 1,
                    transition: 'opacity 0.4s ease-in-out'
                  }}
                />
              </>
            )}
          </div>
        )}

        {/* Asset layer (if needed) */}
        {showAsset && (
          <div className="asset-wrapper">
            <div
              className={`asset-inner ${!assetImageLoaded ? 'loading-tiny' : ''}`}
              style={{ backgroundImage: `url(${assetCurrentImageUrl})` }}
            >
              {/* Structural img provides sizing, background-image provides visual display at 106% */}
              <img
                src={slide.src}
                alt={slide.alt}
                className="carousel-structural-image"
                style={{
                  width: slide.size,
                  height: slide.size,
                  objectFit: slide.fit || 'contain'
                }}
                data-project={projectId}
                data-media={slide.mediaId}
                data-media-type="image"
                data-media-title={slide.title}
              />
            </div>
          </div>
        )}

        <div className="loading-media" style={{ display: 'none' }}>
          Loading media...
        </div>
      </div>
    );
  };

  return (
    /* carousel-container */
    <div className="section-container" data-project={projectId}>
      {/* Carousel Container */}
      <div 
        className={`carousel-container ${carouselHeightClass}`}
      >
        {/* Carousel Grid - 2 column layout */}
        <div className="carousel-grid">
          {/* News Column - Left side 30% */}
          <div className="news-column">
            {/* Top row - 30% height (where expanded icon visually aligns) */}
            <div className="news-top">
              {project['name-asset'] ? (
                <img src={`${ASSETS_BASE_URL}${project['name-asset']}`} alt={project.name || 'Logo'} />
              ) : project.name ? (
                <h1>{project.name}</h1>
              ) : null}
            </div>
            {/* Bottom row - Auto height (news updates) */}
            <div className="news-bottom">
              <div className="hello">
                {project['hello-1st'] && <h3>{preventOrphans(project['hello-1st'])}</h3>}
                {project['hello-2nd'] && <p>{preventOrphans(project['hello-2nd'])}
                  {/*
                  <br /><br />See my art in action on <a href="https://www.youtube.com/@dyararts" target="_blank" rel="noopener noreferrer">YouTube</a>!
                  */}
                  </p>}
              </div>
              {/* News items - shown ONLY for hero-order - Show only latest */}
              {project['filter-method'] === 'hero-order' && newsData && newsData.length > 0 && (
                <div className="news-feed news-item-desktop">
                  {/* Show only the most recent news item */}
                  {(() => {
                    const item = newsData[0];
                    const thumbnailUrl = getThumbnailUrl(item.thumbnail);
                    const newsItemContent = (
                      <>
                        {/* Row 1: Category and Date */}
                        <div className="news-item-meta">
                          {item.Category && (
                            <div className="news-category">{item.Category}</div>
                          )}
                          {item.Date && (
                            <div className="news-date">
                              {(() => {
                                // Parse date like "Nov 4, 2025" or "Oct 1, 2024"
                                const dateMatch = item.Date.match(/^(\w+)\s+(\d+),\s+(\d+)$/);
                                if (dateMatch) {
                                  const [, month, day, year] = dateMatch;
                                  return (
                                    <>
                                      {month} {day}, {year}
                                    </>
                                  );
                                }
                                return item.Date; // Fallback if parsing fails
                              })()}
                            </div>
                          )}
                        </div>
                        
                        {/* Row 2: Headline */}
                        {item.Headline && (
                          <h4 className="news-headline">{parseMarkdown(item.Headline)}</h4>
                        )}
                        
                        {/* Row 3: Content and Thumbnail */}
                        <div className="news-content-wrapper">
                          {item.Content && (
                            <p className="news-content">{parseMarkdown(preventOrphans(item.Content, 6))}</p>
                          )}
                          {thumbnailUrl && (
                            <div className="news-thumbnail-wrapper">
                              <img 
                                src={thumbnailUrl} 
                                alt={item.Headline || 'News thumbnail'} 
                                className="news-thumbnail"
                              />
                              <span className="btn-nav">→</span>
                            </div>
                          )}
                        </div>
                      </>
                    );

                    // If there's a link, wrap the entire news-item in an anchor
                    if (item.Link) {
                      return (
                        <a 
                          key={0}
                          href={item.Link} 
                          className={`news-item has-link ${thumbnailUrl ? 'has-thumbnail' : ''}`}
                        >
                          {newsItemContent}
                        </a>
                      );
                    }

                    return (
                      <div key={0} className={`news-item ${thumbnailUrl ? 'has-thumbnail' : ''}`}>
                        {newsItemContent}
                      </div>
                    );
                  })()}
                  {/* More News Link - outside news-item */}
                  <a href="/news" className="news-more-link">
                    More News →
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Carousel Asset View - Right side 70% */}
          <div className="carousel-asset-view w-full h-full relative z-10">
          
          {/* Project Details - Show for all slides */}
          {currentSlide < slides.length && (
            <div className="project-details">
              <h2>{preventOrphans(slides[currentSlide].captionTitle)}</h2>
              {/* <p>{preventOrphans(slides[currentSlide].captionDescription)}</p> */}
            </div>
          )}
          {/* Main Asset Container */}
          <div
            ref={carouselRef}
            className="relative w-full h-full"
            data-project={projectId}
          >
            {currentSlide < slides.length && (
              <>
                {/* Wrap slide in clickable link if it's an artwork with a page */}
                {slides[currentSlide].type !== 'video' && getSlideLink(slides[currentSlide]) ? (
                  <a
                    href={getSlideLink(slides[currentSlide])}
                    key={`slide-${currentSlide}`}
                    className="slide-fade-in carousel-slide-link"
                    style={{
                      width: '100%',
                      height: '100%',
                      animation: 'slideContentFadeIn 0.2s ease-in-out',
                      display: 'block',
                      position: 'relative',
                      cursor: 'pointer'
                    }}
                    aria-label={`View ${slides[currentSlide].captionTitle || slides[currentSlide].title}`}
                  >
                    {renderSlideContent(slides[currentSlide])}
                    {/* Hover overlay with sub-headline */}
                    <div className="carousel-hover-overlay">
                      {slides[currentSlide].subHeadline && (
                        <div className="carousel-hover-subheadline">
                          {preventOrphans(slides[currentSlide].subHeadline, 6)}
                        </div>
                      )}
                    </div>
                  </a>
                ) : (
                  <div
                    key={`slide-${currentSlide}`}
                    className="slide-fade-in"
                    style={{
                      width: '100%',
                      height: '100%',
                      animation: 'slideContentFadeIn 0.2s ease-in-out'
                    }}
                  >
                    {renderSlideContent(slides[currentSlide])}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Simple Paused Overlay - Only show when video is paused and ready */}
          {currentSlide < slides.length && slides[currentSlide].type === 'video' && !isVideoPlaying && !isDragging && videoRef.current && videoRef.current.readyState >= 3 && (
            <div
              className="video-paused-overlay"
              onClick={toggleVideoPlayback}
              style={{ cursor: 'pointer' }}
            >
              <div className="paused-overlay-content">
                <div className="play-icon">
                &#8227;
                </div>
                <div className="paused-text">Press to Play</div>
              </div>
            </div>
          )}

          {/* UI Controls Wrapper - Contains video controls and pagination */}
          <div className="ui-controls-wrapper">
              {/* Video Controls Container - Always present, content conditional */}
          <div 
            className={`video-controls-container ${showVideoControls ? 'show' : 'hide'}`}
          >
            {/* Content only renders on video slides */}
            {showVideoControls && (
              <div className="video-controls-content">
                  <div 
                    className={`video-scrubber-container ${isDragging ? 'dragging' : ''}`}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseLeave}
                    onClick={handleVideoSeek}
                    data-project={projectId}
                    data-action="video-seek"
                    data-media={currentSlide < slides.length && slides[currentSlide] ? slides[currentSlide].mediaId : ''}
                  >
                    <div 
                      className={`video-scrubber-progress ${videoProgress >= (window.innerWidth <= 768 ? 20 : 10) ? 'show-text' : ''}`}
                      style={{ width: `${videoProgress}%` }}
                    >
                      <div className="video-progress-text">
                        {Math.round(videoProgress)}
                      </div>
                    </div>
                    {/* Scrubber handle */}
                    <div 
                      className="video-scrubber-handle"
                      style={{ left: `calc(${videoProgress}% - 5px)` }}
                    ></div>
                </div>
                
                {/* Sound On/Off Button OR No Sound Button */}
                {hasAudioTrack ? (
                  <button
                    onClick={toggleLocalMute}
                    className="video-global-mute-button"
                    data-project={projectId}
                    data-action="toggle-local-mute"
                  >
                    {isLocalMuted ? 'Muted' : <>Audio ON</>}
                  </button>
                ) : (
                  <button
                    className="video-no-sound-button"
                    disabled
                    data-project={projectId}
                    data-action="no-sound-indicator"
                  >
                    No Sound
                  </button>
                )}
              </div>
            )}
          </div>

        {/* Pagination Navigation - Bottom */}
        {slides && slides.length > 0 && (
          <div className="pagination-navigation">
            {/* Left Arrow - Previous Button */}
            <button
              onClick={() => {
                // If on first slide, loop to last slide
                // Otherwise go to previous slide
                if (currentSlide === 0) {
                  goToSlide(slides.length - 1);
                } else {
                  goToSlide(currentSlide - 1);
                }
              }}
              className="indicator-buttons"
              data-project={projectId}
              data-action="previous-button"
            >
              &larr;
              <div></div>
            </button>
            
            {/* Pagination Container */}
            <div 
              ref={paginationContainerRef}
              className="pagination-container"
              style={{ 
                width: isPaginationMinimal ? 'auto' : `${VISIBLE_BUTTONS * BUTTON_WIDTH}px`
              }}
            >
              {isPaginationMinimal ? (
                /* Minimal View - Slide Counter */
                <div className="indicator-buttons-static px-16">
                  {currentSlide + 1}<span className='text-lg text-gray-400'>&nbsp;/&nbsp;</span>{slides.length}
                </div>
              ) : (
                /* Full View - Slide Number Indicators */
                slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      if (index === currentSlide && slides[currentSlide].type === 'video') {
                        toggleVideoPlayback();
                      } else {
                        goToSlide(index);
                      }
                    }}
                    className={`indicator-buttons ${
                      slides[index].type === 'video' ? 'indicator-buttons-video' : ''
                    } ${
                      index === currentSlide
                        ? 'active'
                        : ''
                    } ${
                      index === currentSlide && slides[currentSlide].type === 'video'
                        ? 'indicator-buttons-playback'
                        : ''
                    }`}
                    data-project={projectId}
                    data-action="go-to-slide"
                    data-slide-index={index}
                  >
                    {index === currentSlide && slides[currentSlide].type === 'video' ? 
                      (isVideoPlaying ? 'Playing' : (
                        <>
                          <span>&#8227;</span>
                        </>
                      )) : 
                      (index + 1)
                    }
                    <div></div>
                  </button>
                ))
              )}
            </div>
            
            {/* Progress Button - Between Pagination and Right Arrow */}
            <button
              onClick={toggleAutoplay}
              className={`autoplay-progress-button ${
                currentSlide < slides.length && slides[currentSlide].type === 'video' ? 'disabled' : ''
              }`}
              data-project={projectId}
              data-action="toggle-autoplay"
              disabled={currentSlide < slides.length && slides[currentSlide].type === 'video'}
            >
                {/* Progress Fill Container - Always visible as UI indicator */}
                <div className="progress-fill-container">
              {/* Progress Fill Background - Show on all non-video slides (images only) */}
              {isGlobalAutoplayActive && activeCarouselId === projectId && 
                currentSlide < slides.length && slides[currentSlide].type !== 'video' && (
                <div 
                  className="progress-fill"
                  style={{ width: `${autoplayProgress}%` }}
                />
              )}
              </div>
              {/* Play/Pause Icon */}
              <span className="progress-icon">
                {currentSlide < slides.length && slides[currentSlide].type === 'video' ? (
                  <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current" style={{ color: '#a7a7a7' }}>
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                ) : isGlobalAutoplayActive ? (
                    <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current" style={{ color: 'var(--tw-myColors-dark1)' }}>
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current" style={{ color: '#a7a7a7' }}>
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                )}
              </span>
            </button>
            
            {/* Right Arrow - Next Button */}
            <button
              onClick={() => {
                // If on last slide, loop back to first slide
                // Otherwise go to next slide
                if (currentSlide === slides.length - 1) {
                  goToSlide(0);
                } else {
                  goToSlide(currentSlide + 1);
                }
              }}
              className="indicator-buttons"
              data-project={projectId}
              data-action="next-button"
            >
              &rarr;
              <div></div>
            </button>
          </div>
        )}
          </div>

          </div>
        </div>
        {/* End Carousel Grid */}

        {/* News Item - Tablet/Mobile version - shown below grid on tablet, hidden on desktop - ONLY for hero-order - Show only latest */}
        {project['filter-method'] === 'hero-order' && newsData && newsData.length > 0 && (
          <div className="content-wrapper news-item-wrapper">
            <div className="news-feed news-item-mobile">
              {/* Show only the most recent news item */}
              {(() => {
                const item = newsData[0];
                const thumbnailUrl = getThumbnailUrl(item.thumbnail);
                const newsItemContent = (
                  <>
                    {/* Row 1: Category and Date */}
                    <div className="news-item-meta">
                      {item.Category && (
                        <div className="news-category">{item.Category}</div>
                      )}
                      {item.Date && (
                        <div className="news-date">
                          {(() => {
                            // Parse date like "Nov 4, 2025" or "Oct 1, 2024"
                            const dateMatch = item.Date.match(/^(\w+)\s+(\d+),\s+(\d+)$/);
                            if (dateMatch) {
                              const [, month, day, year] = dateMatch;
                              return (
                                <>
                                  {month} {day}, {year}
                                </>
                              );
                            }
                            return item.Date; // Fallback if parsing fails
                          })()}
                        </div>
                      )}
                    </div>
                    
                    {/* Row 2: Headline */}
                    {item.Headline && (
                      <h4 className="news-headline">{parseMarkdown(item.Headline)}</h4>
                    )}
                    
                    {/* Row 3: Content and Thumbnail */}
                    <div className="news-content-wrapper">
                      {item.Content && (
                        <p className="news-content">{parseMarkdown(preventOrphans(item.Content, 6))}</p>
                      )}
                      {thumbnailUrl && (
                        <div className="news-thumbnail-wrapper">
                          <img 
                            src={thumbnailUrl} 
                            alt={item.Headline || 'News thumbnail'} 
                            className="news-thumbnail"
                          />
                          <span className="btn-nav">→</span>
                        </div>
                      )}
                    </div>
                  </>
                );

                // If there's a link, wrap the entire news-item in an anchor
                if (item.Link) {
                  return (
                    <a 
                      key={0}
                      href={item.Link} 
                      className={`news-item has-link ${thumbnailUrl ? 'has-thumbnail' : ''}`}
                    >
                      {newsItemContent}
                    </a>
                  );
                }

                return (
                  <div key={0} className={`news-item ${thumbnailUrl ? 'has-thumbnail' : ''}`}>
                    {newsItemContent}
                  </div>
                );
              })()}
              {/* More News Link - outside news-item */}
              <a href="/news" className="news-more-link">
                More News →
              </a>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

function HomePage({
  projectsData, setProjectsData,
  assetsData, setAssetsData,
  heroIds, setHeroIds,
  galleriesData, setGalleriesData,
  newsData, setNewsData,
  isLoading, setIsLoading
}) {
  const [preloadedCarousels, setPreloadedCarousels] = useState(new Set());
  
  // Gallery sort state - track sort order for each gallery by ID
  const [gallerySortOrders, setGallerySortOrders] = useState({});
  
  // Function to get sort order for a gallery (default to 'latest')
  const getGallerySortOrder = (galleryId) => {
    return gallerySortOrders[galleryId] || 'latest';
  };
  
  // Function to set sort order for a gallery
  const setGallerySortOrder = (galleryId, sortOrder) => {
    setGallerySortOrders(prev => ({
      ...prev,
      [galleryId]: sortOrder
    }));
  };
  
  // Preload assets for a carousel
  const preloadCarouselAssets = useCallback((project, assets) => {
    const projectAssets = assets.filter(asset =>
      asset && asset['project-id'] === project['project-id']
    );

    // Preload slides 1-3 immediately (critical path)
    const slidesToPreload = projectAssets.slice(0, 3);

    slidesToPreload.forEach(asset => {
      // Preload main asset
      if (asset.filename) {
        const assetUrl = buildAssetUrl(asset.filename, '');
        const assetType = asset.type || getAssetType(asset.filename, '');

        if (assetType === 'video') {
          // Preload video
          const video = document.createElement('video');
          video.src = assetUrl;
          video.preload = 'metadata';
        } else {
          // Preload image
          const img = new Image();
          img.src = assetUrl;
        }
      }

      // Preload background images (tiny and full)
      if (asset['hero-background-tiny']) {
        const tinyImg = new Image();
        tinyImg.src = `${ASSETS_BASE_URL}${asset['hero-background-tiny']}`;
      }
      if (asset['hero-background']) {
        const bgImg = new Image();
        bgImg.src = `${ASSETS_BASE_URL}${asset['hero-background']}`;
      }
    });

    console.log(`Preloaded slides 1-3 for ${project.name}`);
  }, []);
  
  // Preload remaining assets gradually (Option A: 1-3 immediate, 4+ staggered)
  const preloadAllCarouselAssets = useCallback((project, assets) => {
    const projectAssets = assets.filter(asset =>
      asset && asset['project-id'] === project['project-id']
    );

    // Skip first 3 as they're already loaded immediately
    const remainingAssets = projectAssets.slice(3);

    // Load remaining assets gradually with 200ms delay between each
    remainingAssets.forEach((asset, index) => {
      setTimeout(() => {
        // Preload main asset
        if (asset.filename) {
          const assetUrl = buildAssetUrl(asset.filename, '');
          const assetType = asset.type || getAssetType(asset.filename, '');

          if (assetType === 'video') {
            // Preload video
            const video = document.createElement('video');
            video.src = assetUrl;
            video.preload = 'metadata';
          } else {
            // Preload image
            const img = new Image();
            img.src = assetUrl;
          }
        }

        // Preload background images (tiny and full)
        if (asset['hero-background-tiny']) {
          const tinyImg = new Image();
          tinyImg.src = `${ASSETS_BASE_URL}${asset['hero-background-tiny']}`;
        }
        if (asset['hero-background']) {
          const bgImg = new Image();
          bgImg.src = `${ASSETS_BASE_URL}${asset['hero-background']}`;
        }
      }, index * 200); // 200ms delay between each asset
    });

    console.log(`Started gradual preload of ${remainingAssets.length} remaining assets for ${project.name}`);
  }, []);
  
  // UI state with localStorage persistence
  const [isGlobalMuted, setIsGlobalMuted] = useState(() => {
    const saved = localStorage.getItem('portfolio-mute-state');
    return saved ? JSON.parse(saved) : true; // Default: muted ON
  });
  
  const [isGlobalAutoplayActive, setIsGlobalAutoplayActive] = useState(() => {
    const saved = localStorage.getItem('portfolio-autoplay-state');
    return saved ? JSON.parse(saved) : true; // Default: autoplay ON
  });

  const [carouselPositions, setCarouselPositions] = useState(() => {
    const saved = localStorage.getItem('portfolio-carousel-positions');
    return saved ? JSON.parse(saved) : {};
  });
  
  const [activeCarouselId, setActiveCarouselId] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPaginationMinimal, setIsPaginationMinimal] = useState(false);
  const [viewportType, setViewportType] = useState(window.innerWidth <= 768 ? 'mobile' : 'desktop');

  // Global mute toggle function with localStorage persistence
  const toggleGlobalMute = () => {
    const newMuteState = !isGlobalMuted;
    setIsGlobalMuted(newMuteState);
    localStorage.setItem('portfolio-mute-state', JSON.stringify(newMuteState));
  };

  // Global autoplay toggle function with localStorage persistence
  const toggleGlobalAutoplay = () => {
    const newAutoplayState = !isGlobalAutoplayActive;
    setIsGlobalAutoplayActive(newAutoplayState);
    localStorage.setItem('portfolio-autoplay-state', JSON.stringify(newAutoplayState));
  };

  // Clear all slide states (for testing first-time user experience)
  // Admin functions moved to /admin page (AdminPage.js)

  // Get current slide position for a carousel
  const getCurrentSlide = (carouselId) => {
    return carouselPositions[carouselId] || 0;
  };

  // Update carousel position with localStorage persistence
  const updateCarouselPosition = (carouselId, slideIndex) => {
    const newPositions = {
      ...carouselPositions,
      [carouselId]: slideIndex
    };
    setCarouselPositions(newPositions);
    localStorage.setItem('portfolio-carousel-positions', JSON.stringify(newPositions));
  };

  // Manual sync function - fetches fresh data from Google Sheets and downloads JSON
  // Sync function moved to /admin page (AdminPage.js)


  // Preload slides 1-3 for all carousels when data loads (immediate)
  useEffect(() => {
    if (projectsData.length > 0 && assetsData.length > 0) {
      projectsData.forEach(project => {
        preloadCarouselAssets(project, assetsData);
      });
    }
  }, [projectsData, assetsData, preloadCarouselAssets]);





  // Admin functions available at /admin (development only)

  // Responsive pagination - automatically set minimal view on mobile
  const screenSizeTimeoutRef = useRef(null);
  
  useEffect(() => {
    const checkScreenSize = () => {
      // Clear any pending timeout
      if (screenSizeTimeoutRef.current) {
        clearTimeout(screenSizeTimeoutRef.current);
      }
      
      // Debounce: wait 300ms after resize stops before processing
      screenSizeTimeoutRef.current = setTimeout(() => {
        const newIsMinimal = window.innerWidth < 768; // md breakpoint
        const wasMinimal = isPaginationMinimal;
        const newViewportType = window.innerWidth <= 768 ? 'mobile' : 'desktop';
        
        // Only update if something actually changed
        if (wasMinimal !== newIsMinimal) {
          setIsPaginationMinimal(newIsMinimal);
        }
        
        // Check if viewport type changed
        if (viewportType !== newViewportType) {
          setViewportType(newViewportType);
          console.log(`Viewport changed to ${newViewportType} - rebuilding slides`);
          
          // Reset all carousel positions to slide 1 when switching between views
          console.log('Responsive view changed - resetting all carousels to slide 1');
          const resetPositions = {};
          projectsData.forEach(project => {
            resetPositions[`${project['project-id']}-desktop`] = 0;
            resetPositions[`${project['project-id']}-mobile`] = 0;
          });
          setCarouselPositions(resetPositions);
          localStorage.setItem('portfolio-carousel-positions', JSON.stringify(resetPositions));
        }
      }, 300); // 300ms debounce delay
    };

    // Check on mount
    checkScreenSize();

    // Listen for resize events with debouncing
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
      if (screenSizeTimeoutRef.current) {
        clearTimeout(screenSizeTimeoutRef.current);
      }
    };
  }, [isPaginationMinimal, projectsData, viewportType]); // Add viewportType to dependencies

  // Menu toggle function
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Intersection Observer for autoplay management
  useEffect(() => {
    // Add a small delay to ensure DOM is fully rendered
    const setupObserver = () => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const projectId = entry.target.getAttribute('data-project');
              // Strip -desktop or -mobile suffix
              const baseProjectId = projectId ? projectId.replace(/-desktop$|-mobile$/, '') : projectId;
              if (false) console.log('DEBUG: Intersection observer triggered for project:', projectId);
              
              // Handle autoplay management - only update if different
              if (false) console.log(`DEBUG: Intersection observer - carousel ${projectId} is intersecting`);
              if (projectId && projectId !== activeCarouselId) {
                if (false) console.log(`DEBUG: Setting activeCarouselId to ${projectId}`);
                setActiveCarouselId(projectId);
                
                // Note: Preloading is handled in a separate useEffect to avoid dependency issues
              }
            } else {
              // Carousel is no longer visible
              const projectId = entry.target.getAttribute('data-project');
              if (false) console.log(`DEBUG: Intersection observer - carousel ${projectId} is no longer intersecting`);
              if (projectId === activeCarouselId) {
                if (false) console.log(`DEBUG: Setting activeCarouselId to null (carousel left)`);
                setActiveCarouselId(null);
              }
            }
          });
        },
        {
          threshold: 0.6, // Trigger when 60% of carousel is visible
          rootMargin: '-15% 0px -15% 0px'
        }
      );

      // Observe all sections with data-project attribute
      const sections = document.querySelectorAll('[data-project]');
      console.log('DEBUG: Found sections to observe:', sections.length);
      sections.forEach(section => {
        console.log('DEBUG: Observing section with data-project:', section.getAttribute('data-project'));
        observer.observe(section);
      });

      return observer;
    };

    // Set up observer with a small delay
    const timeoutId = setTimeout(() => {
      const observer = setupObserver();
      return () => observer.disconnect();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [viewportType]); // viewportType triggers re-observation when carousels rebuild after resize

  // Helper function to determine which project is currently most visible
  const getCurrentVisibleProject = useCallback(() => {
    const sections = document.querySelectorAll('[data-project]');
    let mostVisibleProject = null;
    let maxVisibility = 0;

    sections.forEach(section => {
      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Calculate how much of the element is visible
      const visibleTop = Math.max(0, rect.top);
      const visibleBottom = Math.min(viewportHeight, rect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibilityRatio = visibleHeight / rect.height;
      
      // Track the element with the highest visibility ratio
      if (visibilityRatio > maxVisibility) {
        maxVisibility = visibilityRatio;
        mostVisibleProject = section.getAttribute('data-project');
      }
    });

    return mostVisibleProject;
  }, []);


  // Hash routing system

  // Separate useEffect to handle gradual preloading (slides 4+) when carousel becomes active
  useEffect(() => {
    if (activeCarouselId && activeCarouselId !== 'header' && !preloadedCarousels.has(activeCarouselId)) {
      const project = projectsData.find(p => p['project-id'] === activeCarouselId);
      if (project) {
        preloadAllCarouselAssets(project, assetsData);
        setPreloadedCarousels(prev => new Set([...prev, activeCarouselId]));
      }
    }
  }, [activeCarouselId, preloadedCarousels, projectsData, assetsData, preloadAllCarouselAssets]);

  // Pause videos in non-active carousels when activeCarouselId changes
  useEffect(() => {
    const timer = setTimeout(() => {
      projectsData.forEach(project => {
        const projectId = project['project-id'];
        
        // If activeCarouselId is null (intro page), pause ALL videos in ALL carousels
        if (activeCarouselId === null) {
          // Query for both desktop and mobile versions of this carousel
          const carouselElements = document.querySelectorAll(`.section-container[data-project^="${projectId}"]`);
          carouselElements.forEach(carouselElement => {
            if (carouselElement) {
              const videos = carouselElement.querySelectorAll('video');
              videos.forEach(video => {
                if (!video.paused) {
                  video.pause();
                }
              });
            }
          });
          return;
        }
        
        // Skip the currently active carousel entirely
        if (projectId === activeCarouselId) {
          return;
        }
        
        // Pause videos in non-active carousels (existing logic)
        // Query for both desktop and mobile versions
        const carouselElements = document.querySelectorAll(`.section-container[data-project^="${projectId}"]`);
        carouselElements.forEach(carouselElement => {
          if (carouselElement) {
            const videos = carouselElement.querySelectorAll('video');
            videos.forEach(video => {
              if (!video.paused) {
                // Check if this is a first-slide video (slide index 0)
                const currentSlideIndex = getCurrentSlide(projectId);
                const isFirstSlideVideo = currentSlideIndex === 0;
                
                // Only pause if it's NOT a first-slide video
                if (!isFirstSlideVideo) {
                  video.pause();
                }
              }
            });
          }
        });
      });
    }, 200);
    
    return () => clearTimeout(timer);
  }, [activeCarouselId, projectsData, getCurrentSlide]);

  return (
    <div className="min-h-screen">
      {/* Admin Links - MOVED TO /admin PAGE (development only) */}

      {/* Navigation Component (App Icon + Slide-out Menu) */}
      <Navigation assetsData={assetsData} />

      {/* Scrollable Container */}
      <div className="h-screen overflow-y-auto">
        {/* Hero Carousel - Featured Assets */}
        {!isLoading && (() => {
          // Get hero project data from projectsData (loaded from JSON)
          const heroProject = projectsData.find(p => p['project-id'] === 'hero');
          if (!heroProject) return null;
          
          // Build slides based on project's filter-method
          const heroSlides = buildCarouselSlides(heroProject, assetsData, heroIds);
          if (heroSlides.length === 0) return null;
          
          return (
            <React.Fragment>
              {/* Desktop Hero Carousel */}
              <div className="carousel-desktop-wrapper">
                <Carousel
                  key={`hero-desktop-${viewportType}`}
                  projectId="hero-desktop"
                  project={heroProject}
                  slides={heroSlides}
                  title="Featured Works"
                  isGlobalMuted={isGlobalMuted}
                  toggleMenu={toggleMenu}
                  isGlobalAutoplayActive={isGlobalAutoplayActive}
                  toggleGlobalAutoplay={toggleGlobalAutoplay}
                  activeCarouselId={activeCarouselId}
                  carouselPositions={carouselPositions}
                  updateCarouselPosition={updateCarouselPosition}
                  getCurrentSlide={getCurrentSlide}
                  isPaginationMinimal={isPaginationMinimal}
                  newsData={newsData}
                  assetsData={assetsData}
                />
              </div>

              {/* Mobile Hero Carousel */}
              <div className="carousel-mobile-wrapper">
                <Carousel
                  key={`hero-mobile-${viewportType}`}
                  projectId="hero-mobile"
                  project={heroProject}
                  slides={heroSlides}
                  title="Featured Works"
                  isGlobalMuted={isGlobalMuted}
                  toggleMenu={toggleMenu}
                  isGlobalAutoplayActive={isGlobalAutoplayActive}
                  toggleGlobalAutoplay={toggleGlobalAutoplay}
                  activeCarouselId={activeCarouselId}
                  carouselPositions={carouselPositions}
                  updateCarouselPosition={updateCarouselPosition}
                  getCurrentSlide={getCurrentSlide}
                  isPaginationMinimal={isPaginationMinimal}
                  newsData={newsData}
                  assetsData={assetsData}
                />
              </div>
            </React.Fragment>
          );
        })()}
        <div className="content-wrapper">
          {/* Dynamic Galleries - render from galleries data */}
          {!isLoading && galleriesData && galleriesData.length > 0 ? (
            galleriesData
              .filter(gallery => gallery.id !== 'gal2') // DISABLED: Hide Inktober 2024 gallery
              .map((gallery, index) => {
              const currentSortOrder = getGallerySortOrder(gallery.id);
              const isBannerGallery = gallery['display-type'] === 'banner';

              // Calculate count of assets in this gallery
              const galleryItemCount = gallery['filter-prefix']
                ? assetsData.filter(asset => {
                    const artId = asset['artwork-id'] || asset.filename.split('_')[0];
                    return artId.startsWith(gallery['filter-prefix']);
                  }).length
                : assetsData.length;

              return (
                <div key={gallery.id || `section-${index}`} className={`section-wrapper section-${index + 1}-container`}>
                  {/* 2x2 Grid Header */}
                  <div className="section-header-grid">
                    {/* Cell 1A: Title (top left) */}
                    <div className="section-header-cell section-header-title">
                      <h2 id={gallery.id}>{gallery.title}</h2>
                    </div>

                    {/* Cell 1B: Artwork Count (top right) */}
                    <div className="section-header-empty">
                    </div>
                    
                    {/* Cell 2A: Description (bottom left) */}
                    <div className="section-header-cell section-header-description">
                      {/* <div className="count-pieces">
                        <h3>{galleryItemCount}</h3>
                        <p>Pieces</p>
                      </div> */}
                      <p>{gallery.description}</p>
                    </div>
                    
                    {/* Cell 2B: Sort Options (bottom right) - Hide for banner galleries */}
                    <div className="section-header-cell section-header-sort">
                      {!isBannerGallery && (
                        <div className="sort-options">
                          SORT:
                          <span 
                            onClick={() => setGallerySortOrder(gallery.id, 'latest')}
                            className={currentSortOrder === 'latest' ? 'active' : ''}
                            style={{ cursor: 'pointer' }}
                          >
                            Latest
                          </span>
                          <span 
                            onClick={() => setGallerySortOrder(gallery.id, 'earliest')}
                            className={currentSortOrder === 'earliest' ? 'active' : ''}
                            style={{ cursor: 'pointer' }}
                          >
                            Earliest
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Render banner for banner-type galleries, masonry for others */}
                  {isBannerGallery ? (
                    <a 
                      href={`/${gallery.slug}`} 
                      className="gallery-banner"
                      style={{
                        backgroundImage: gallery['banner-asset'] 
                          ? `url('${ASSETS_BASE_URL}${gallery['banner-asset']}')` 
                          : 'none'
                      }}
                    >
                      <div className="gallery-banner-overlay">
                        <span className="gallery-banner-text">see more</span>
                      </div>
                    </a>
                  ) : (
                    <MasonryGallery 
                      assets={assetsData} 
                      filterPrefix={gallery['filter-prefix']}
                      sortOrder={currentSortOrder}
                    />
                  )}
                </div>
              );
            })
          ) : !isLoading ? (
            <div className="text-center py-8 text-gray-500">No galleries available</div>
          ) : (
            <div className="text-center py-8 text-gray-500">Loading galleries...</div>
          )}

          {/* Copyright Notice */}
          <div className="copyright-notice">
            <p>© 2025 DYAR Arts LLC. All rights reserved. All artwork and content on this site are protected by copyright. Unauthorized use, reproduction, distribution, or use for AI training, machine learning, or data scraping is strictly prohibited without express written permission. For licensing inquiries or collaborations, please <Link to="/about#contact">contact me</Link>.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Animated Routes wrapper (must be inside Router to use useLocation)
function AnimatedRoutes({ projectsData, assetsData, heroIds, galleriesData, newsData, isLoading, setProjectsData, setAssetsData, setHeroIds, setGalleriesData, setNewsData, setIsLoading }) {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <HomePage
              projectsData={projectsData}
              setProjectsData={setProjectsData}
              assetsData={assetsData}
              setAssetsData={setAssetsData}
              heroIds={heroIds}
              setHeroIds={setHeroIds}
              galleriesData={galleriesData}
              setGalleriesData={setGalleriesData}
              newsData={newsData}
              setNewsData={setNewsData}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />
          }
        />
        <Route
          path="/art/:slug"
          element={
            <TemplateFrame assetsData={assetsData}>
              <ArtworkPage
                assets={assetsData}
                baseUrl={ASSETS_BASE_URL}
              />
            </TemplateFrame>
          }
        />
        <Route
          path="/about"
          element={
            <TemplateFrame assetsData={assetsData}>
              <AboutPage />
            </TemplateFrame>
          }
        />
        <Route
          path="/news"
          element={
            <TemplateFrame assetsData={assetsData}>
              <NewsPage newsData={newsData} assetsData={assetsData} />
            </TemplateFrame>
          }
        />
        <Route
          path="/admin"
          element={<AdminPage />}
        />
        {/* Dynamic routes for banner-type galleries */}
        {galleriesData
          .filter(gallery => gallery['display-type'] === 'banner' && gallery.slug && gallery.id !== 'gal2') // DISABLED: Hide Inktober 2024 route
          .map((gallery) => {
            // Find matching project based on filter-method matching filter-prefix
            const project = projectsData.find(p => p['filter-method'] === gallery['filter-prefix']);

            return (
              <Route
                key={gallery.slug}
                path={`/${gallery.slug}`}
                element={
                  <TemplateFrame assetsData={assetsData}>
                    <GalleryCarouselPage
                      assets={assetsData}
                      gallery={gallery}
                      project={project}
                    />
                  </TemplateFrame>
                }
              />
            );
          })
        }
        {/* Amplify 3 download pages */}
        <Route
          path="/amplify3"
          element={
            <TemplateFrame assetsData={assetsData}>
              <Amplify3Page />
            </TemplateFrame>
          }
        />
        <Route
          path="/amplify3/download"
          element={
            <TemplateFrame assetsData={assetsData}>
              <Amplify3DownloadPage />
            </TemplateFrame>
          }
        />
        {/* 404 Catch-all route - must be last */}
        <Route
          path="*"
          element={
            <div className="page-not-found">
              <h1>Page Not Found</h1>
              <h2>404</h2>
              <p>
                The page you're looking for doesn't exist. It might have been moved or deleted.
              </p>
              <button className="btn" onClick={() => window.location.href = '/'}>Back to Home</button>
            </div>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

// App wrapper with Router and shared data
function App() {
  // Data loaded from JSON file - will be updated by sync button
  const [projectsData, setProjectsData] = useState([]);
  const [assetsData, setAssetsData] = useState([]);
  const [heroIds, setHeroIds] = useState([]);
  const [galleriesData, setGalleriesData] = useState([]);
  const [newsData, setNewsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load data from JSON file on app start
  useEffect(() => {
    const loadPortfolioData = () => {
      try {
        console.log('Loading portfolio data from JSON file...');
        setProjectsData(portfolioData.projects || []);
        setAssetsData(portfolioData.assets || []);
        setHeroIds(portfolioData.hero || []);
        setGalleriesData(portfolioData.galleries || []);
        setNewsData(portfolioData.news || []);
        setIsLoading(false);
        console.log('Portfolio data loaded:', portfolioData.projects?.length || 0, 'projects,', portfolioData.assets?.length || 0, 'assets,', portfolioData.hero?.length || 0, 'hero IDs,', portfolioData.galleries?.length || 0, 'galleries,', portfolioData.news?.length || 0, 'news items');
      } catch (error) {
        console.error('Error loading portfolio data:', error);
        setIsLoading(false);
      }
    };

    loadPortfolioData();
  }, []);

  return (
    <Router>
      <AnimatedRoutes
        projectsData={projectsData}
        setProjectsData={setProjectsData}
        assetsData={assetsData}
        setAssetsData={setAssetsData}
        heroIds={heroIds}
        setHeroIds={setHeroIds}
        galleriesData={galleriesData}
        setGalleriesData={setGalleriesData}
        newsData={newsData}
        setNewsData={setNewsData}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
      />
    </Router>
  );
}

export default App;
